import { apiClient } from '../client'
import type {
  DnsOverride,
  DnsOverrideStatus,
  PaginatedDnsOverrides,
} from '../../app/modules/types/dnsOverride'

const BASE = '/dns-overrides/'

/**
 * Builds the list query string.
 *
 * `page_size` is intentionally never sent: the backend caps it at 10 and
 * ignores anything larger. Page 1 is omitted so the default view and an
 * explicit "page 1" share one React Query cache entry.
 */
export function buildDnsOverrideParams(params: {
  search?: string
  page?: number
}): string {
  const searchParams = new URLSearchParams()
  const search = params.search?.trim()
  if (search) searchParams.set('search', search)
  if (params.page && params.page > 1) searchParams.set('page', String(params.page))
  return searchParams.toString()
}

/** Payload for `create`. No `id`/`created_at` — the domain IS the key (see DnsOverride). */
export type CreateDnsOverridePayload = Pick<DnsOverride, 'domain' | 'ip'> & { ttl?: number }

/** Payload for `update`. The domain cannot be renamed — PUT is not implemented backend-side. */
export type UpdateDnsOverridePayload = Partial<Omit<DnsOverride, 'domain'>>

export const dnsOverridesApi = {
  /** Feature/health probe. Never returns 503 — this is what the UI gates on. */
  status: async (): Promise<DnsOverrideStatus> => {
    const { data } = await apiClient.get(`${BASE}etcd-status/`)
    return data
  },

  list: async (params?: string): Promise<PaginatedDnsOverrides> => {
    const { data } = await apiClient.get(params ? `${BASE}?${params}` : BASE)
    return data
  },

  /** NOT an upsert — an existing or overlapping domain returns 409. */
  create: async (body: CreateDnsOverridePayload): Promise<DnsOverride> => {
    const { data } = await apiClient.post(BASE, body)
    return data
  },

  /** PUT is not implemented backend-side. The domain cannot be renamed. */
  update: async (
    domain: string,
    body: UpdateDnsOverridePayload
  ): Promise<DnsOverride> => {
    const { data } = await apiClient.patch(`${BASE}${encodeURIComponent(domain)}/`, body)
    return data
  },

  /** Idempotent: deleting a nonexistent domain also returns 204. */
  remove: async (domain: string): Promise<void> => {
    await apiClient.delete(`${BASE}${encodeURIComponent(domain)}/`)
  },
}
